import '../app/shared/sentry/init'

import { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { ErrorBoundary } from 'react-error-boundary'
import { Outlet, RouterProvider, ScrollRestoration, createBrowserRouter } from 'react-router-dom'

import '../../styles/charts.css'
import '../../styles/modal.css'
import '../../styles/tailwind.css'
import '../../styles/timeframe-bar.css'
import QuoteRouteSwitch, { getQuoteRouteLoader, getShouldRevalidateQuoteRoute } from './QuoteRoutesSwitch'
import { DefaultErrorBoundary, RouterErrorElement } from './components/error-view'
import { LoadingScreen } from './components/loading'
import { NotificationWrapper } from './components/notification'
import { SettingsContext } from './context'
import { CachedLoaderDataContextProvider } from './hooks/useCachedLoaderData'
import { lazyLoadComponent, revalidateOnQueryChange } from './util'

const ChartsRoute = lazyLoadComponent({
  chunkName: 'charts',
  load: () => import(/* webpackChunkName: "charts" */ './modules/charts'),
})

const getPortfolioBundle = () => import(/* webpackChunkName: "portfolio" */ './modules/portfolio')
const PortfolioViewRoute = lazyLoadComponent<unknown>({
  chunkName: 'portfolio',
  load: () => getPortfolioBundle().then((mod) => mod.portfolioView),
})
const PortfolioCreateRoute = lazyLoadComponent<unknown>({
  chunkName: 'portfolio',
  load: () => getPortfolioBundle().then((mod) => mod.portfolioEmpty),
})

const getPortfolioEditBundle = () =>
  import(/* webpackChunkName: "portfolio-edit" */ './modules/portfolio/routes/PortfolioBulkEdit')
const PortfolioEditRoute = lazyLoadComponent({
  chunkName: 'portfolio-edit',
  load: getPortfolioEditBundle,
})

const getSaveToPortfolioBundle = () => import(/* webpackChunkName: "save-to-portfolio" */ './modules/save-to-portfolio')
const SaveToPortfolioRoute = lazyLoadComponent({
  chunkName: 'save-to-portfolio',
  load: getSaveToPortfolioBundle,
})

const getCreateAlertBundle = () => import(/* webpackChunkName: "create-alert" */ './modules/create-alert')
const CreateAlertRoute = lazyLoadComponent({
  chunkName: 'create-alert',
  load: getCreateAlertBundle,
})

const router = createBrowserRouter([
  {
    path: '/',
    element: <Outlet />,
    errorElement: <RouterErrorElement />,
    children: [
      {
        path: '/charts',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <ChartsRoute />
          </Suspense>
        ),
      },
      {
        path: '/portfolio.ashx',
        loader: (args) => getPortfolioBundle().then((value) => value.portfolioView.loader(args)),
        action: (args) => getPortfolioBundle().then((value) => value.portfolioView.action(args)),
        shouldRevalidate: revalidateOnQueryChange(['pid']),
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <CachedLoaderDataContextProvider>
              <PortfolioViewRoute />
            </CachedLoaderDataContextProvider>
          </Suspense>
        ),
      },
      {
        path: '/create_new_portfolio.ashx',
        loader: () => getPortfolioBundle().then((value) => value.portfolioEmpty.loader()),
        action: (args) => getPortfolioBundle().then((value) => value.portfolioEmpty.action(args)),
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <CachedLoaderDataContextProvider>
              <PortfolioCreateRoute />
            </CachedLoaderDataContextProvider>
          </Suspense>
        ),
      },
      {
        path: '/portfolio_edit.ashx',
        loader: (args) => getPortfolioEditBundle().then((value) => value.loader(args)),
        action: (args) => getPortfolioEditBundle().then((value) => value.action(args)),
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <CachedLoaderDataContextProvider>
              <PortfolioEditRoute />
            </CachedLoaderDataContextProvider>
          </Suspense>
        ),
      },
      {
        path: '/quote.ashx',
        loader: getQuoteRouteLoader,
        shouldRevalidate: getShouldRevalidateQuoteRoute(),
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <CachedLoaderDataContextProvider>
              <QuoteRouteSwitch />
            </CachedLoaderDataContextProvider>
            {/**
             * We precompute the element height on the backend so that the content under
             * options doesn’t jump when options render, so we can put restore scroll here.
             */}
            <ScrollRestoration />
          </Suspense>
        ),
      },
      {
        path: '/save_to_portfolio.ashx',
        loader: () => getSaveToPortfolioBundle(),
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <SaveToPortfolioRoute />
          </Suspense>
        ),
      },
      {
        path: '/create_alert.ashx',
        loader: () => getCreateAlertBundle(),
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <CreateAlertRoute />
          </Suspense>
        ),
      },
    ],
  },
])

const rootEl = document.getElementById('root')
const root = ReactDOM.createRoot(rootEl!)

root.render(
  <SettingsContext.Provider value={FinvizSettings}>
    <ErrorBoundary FallbackComponent={DefaultErrorBoundary}>
      <NotificationWrapper>
        <RouterProvider router={router} />
      </NotificationWrapper>
    </ErrorBoundary>
  </SettingsContext.Provider>
)
